import React, { ChangeEvent, Component, MouseEventHandler, ReactNode } from 'react'

import { LetterAttached, LetterRequest } from 'component-library-next'
import { IDocument } from 'component-library-next/types/DocumentList'

import { Case } from '@app/graphql/types'
import { ModernChatHeader } from '@app/lib/components/ModernChat/ModernChatHeader/ModernChatHeader'

import { AppointmentDate } from './AppointmentDate'
import { MessageContent } from './MessageContent'
import { MessageEditor } from './MessageEditor'
import { ModernChatAvatar } from './ModernChatAvatar'
import { RecommendedTemplate } from './RecommendedTemplate/RecommendedTemplate'

import './index.scss'

export interface IUser {
  profilePictureUrl?: string
  firstName?: string
  lastName?: string
  title?: string
}

export interface ITopic {
  id?: number
  name?: string
}
export interface IComment {
  id?: number
  comment?: string
  updatedAt?: Date
}
export interface IMessage {
  appointmentAt?: Date
  customer?: IUser
  case?: Case
  documents?: IDocument[]
  lawyer?: IUser
  updatedAt?: Date
  content?: string
  type: MessageType
  id?: number
  /**
   * If present it will be displayed in the chat component header (modern-chat__header)
   */
  topic?: ITopic
  /**
   * If present it will be displayed below the chat main message (only if type = question)
   */
  serviceComment?: IComment
}

type Props = {
  badges?: ReactNode
  children?: ReactNode
  collapsesLongText?: boolean
  deleteMessage?: (messageId: number) => void
  showAttachedDocuments?: boolean
  onStartEdit?: MouseEventHandler
  onFinishEdit?: (updatedText: string) => void
  onAddDocument?: (categoryId: number) => void | MouseEventHandler
  onRemove?: (documentId) => any
  shortenName?: boolean
  displayLetterRequest?: boolean
  letterRequestGreyOut?: boolean
  letterRequestChecked?: boolean
  letterRequestDisabled?: boolean
  letterRequestAlternativeText?: string
  displayLetterAttached?: boolean
  letterAttachedAlternativeText?: string
  onLetterRequestChange?: (e: ChangeEvent<HTMLInputElement>) => void
  message?: IMessage
  nowrap?: boolean
  documentsDisabled?: boolean
  testId?: string
  displayRecommendedTemplate?: boolean
  recommendedTemplateLink?: string
  recommendedTemplateName?: string
}

export enum MessageType {
  Question = 'Question',
  Answer = 'Answer',
  Information = 'Information'
}

export enum MessageTypeTitle {
  Answer = 'Antwort',
  Information = 'Information'
}

export class ModernChat extends Component<Props> {
  static defaultProps: Props = {
    displayLetterRequest: false,
    documentsDisabled: false
  }

  render() {
    const {
      badges,
      children,
      collapsesLongText,
      deleteMessage,
      message = {} as Partial<IMessage>,
      onFinishEdit,
      onStartEdit,
      displayLetterRequest,
      letterRequestChecked,
      letterRequestDisabled,
      letterRequestGreyOut,
      letterRequestAlternativeText,
      displayLetterAttached,
      letterAttachedAlternativeText,
      onLetterRequestChange,
      nowrap,
      testId,
      recommendedTemplateLink,
      recommendedTemplateName
    } = this.props
    const isQuestion = message.type === MessageType.Question
    const user = isQuestion ? message.customer : message.lawyer
    return (
      <div
        data-testid={testId}
        className={`
        ${!nowrap ? `modern-chat__wrapper` : ''}
        ${
          !nowrap && message.type
            ? `modern-chat__wrapper--${isQuestion ? 'question' : 'answer'}`
            : ''
        }
      `}
      >
        <AppointmentDate appointmentAt={message.appointmentAt} />
        <ModernChatHeader badges={badges} message={message} />

        <div className='modern-chat__message'>
          {user && <ModernChatAvatar user={user} />}

          {onFinishEdit ? (
            <MessageEditor onFinishEdit={onFinishEdit} initialMessage={message} />
          ) : (
            <MessageContent
              deleteMessage={deleteMessage}
              message={message}
              collapsesLongText={collapsesLongText}
              onStartEdit={onStartEdit}
            />
          )}
        </div>
        {displayLetterRequest && (
          <LetterRequest
            checked={letterRequestChecked}
            disabled={letterRequestDisabled}
            greyOut={letterRequestGreyOut}
            onChange={onLetterRequestChange}
            text={letterRequestAlternativeText}
          />
        )}
        {recommendedTemplateName && (
          <RecommendedTemplate
            templateLink={recommendedTemplateLink}
            templateName={recommendedTemplateName}
          />
        )}
        {displayLetterAttached && <LetterAttached text={letterAttachedAlternativeText} />}
        {children && <div className={'modern-chat__children'}>{children}</div>}
      </div>
    )
  }
}
